import React, { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, ChevronDown, ExternalLink } from 'react-feather';
import { useAuth, normalizeUserType } from 'Utils/Hooks/useAuth';
import { adminNav, partnerNav, pendingPartnerNav } from './navigation';
import { filterNav, useRefreshPartnerFeatures } from './features';
import { filterSuperadminNav } from 'Shared/BillingExceptions/helpers';
import { Loading, Pill } from 'Shared/UI';
import WelcomeGuide from 'Help/WelcomeGuide';
import HelpMenu from 'Help/HelpMenu';
import { useLogTracer } from 'Shared/LogTracer';

/** Menu entries that act instead of navigating (e.g. open the log tracer in a new tab). */
export const NavAction = ({ label, action, icon: Icon }) => {
  const [openTracer, busy] = useLogTracer();
  const run = { 'log-tracer': () => openTracer() }[action];
  return (
    <button
      type="button"
      className="ed-nav__link ed-nav__action"
      onClick={run}
      disabled={busy || !run}
      title={`${label} (opens in a new tab)`}
      aria-label={`${label} (opens in a new tab)`}
    >
      <Icon size={16} aria-hidden="true" />
      <span>{label}</span>
      <ExternalLink size={12} aria-hidden="true" className="ed-nav__ext" />
    </button>
  );
};

export const isPendingPartner = (user) =>
  normalizeUserType(user?.user_type) === 'USER' && user?.account_status && user.account_status !== 'active';

const navFor = (user) => {
  if (normalizeUserType(user?.user_type) === 'ADMIN') return filterSuperadminNav(adminNav, user);
  return isPendingPartner(user) ? pendingPartnerNav : filterNav(partnerNav, user);
};

const Brand = () => (
  <div className="ed-brand">
    <span className="ed-brand__mark" aria-hidden="true">E</span>
    <span className="ed-brand__name">Eudor</span>
    <span className="ed-brand__sub">Aggregator</span>
  </div>
);

const NavGroup = ({ group, items, startOpen }) => {
  const [open, setOpen] = useState(startOpen);
  useEffect(() => {
    if (startOpen) setOpen(true);
  }, [startOpen]);
  const id = `nav-${group.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="ed-nav__group">
      <button
        type="button"
        className="ed-nav__group-btn"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{group}</span>
        <ChevronDown size={14} className={open ? 'is-open' : ''} aria-hidden="true" />
      </button>
      <ul id={id} className="ed-nav__list" hidden={!open}>
        {items.map(({ label, to, icon: Icon, exact, action }) => (
          <li key={to || action}>
            {action ? (
              <NavAction label={label} action={action} icon={Icon} />
            ) : (
              <NavLink to={to} end={exact} className={({ isActive }) => `ed-nav__link ${isActive ? 'is-active' : ''}`}>
                <Icon size={16} aria-hidden="true" />
                <span>{label}</span>
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const AppShell = () => {
  const { currentAdmin, setCurrentAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const nav = navFor(currentAdmin);

  // Close the drawer when the route changes or Escape is pressed.
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    document.body.classList.toggle('ed-drawer-open', open);
  }, [open]);

  const groupIsActive = (items) => items.some((i) => i.to && location.pathname.startsWith(i.to.replace(/\/$/, '')));
  const isAdmin = normalizeUserType(currentAdmin?.user_type) === 'ADMIN';
  const pending = isPendingPartner(currentAdmin);
  useRefreshPartnerFeatures(currentAdmin, !isAdmin && !pending, setCurrentAdmin);

  return (
    <div className="ed-shell">
      <a href="#ed-main" className="ed-skip">Skip to content</a>
      <aside className={`ed-sidebar ${open ? 'is-open' : ''}`} aria-label="Main navigation">
        <div className="ed-sidebar__top">
          <Brand />
          <button type="button" className="ed-icon-btn ed-only-sm" aria-label="Close menu" onClick={() => setOpen(false)}>
            <X size={20} />
          </button>
        </div>
        <nav className="ed-nav">
          {nav.map((g, i) => (
            <NavGroup key={g.group} group={g.group} items={g.items} startOpen={i === 0 || groupIsActive(g.items)} />
          ))}
        </nav>
        <div className="ed-sidebar__foot">
          <div className="ed-user">
            <span className="ed-user__name">{currentAdmin?.name || currentAdmin?.email}</span>
            <span className="ed-user__role">{isAdmin ? 'Operations' : 'Partner'}</span>
          </div>
          <button type="button" className="ed-nav__link ed-logout" onClick={() => navigate(`${process.env.PUBLIC_URL || ''}/logout`)}>
            <LogOut size={16} aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
      <div className={`ed-scrim ${open ? 'is-open' : ''}`} onClick={() => setOpen(false)} aria-hidden="true" />

      <div className="ed-main-col">
        <header className="ed-topbar">
          <button type="button" className="ed-icon-btn ed-only-sm" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)}>
            <Menu size={22} />
          </button>
          <div className="ed-only-sm">
            <Brand />
          </div>
          <div className="ed-topbar__end">
            {pending && <Pill status={currentAdmin.account_status} />}
            <span className="ed-topbar__email ed-hide-sm">{currentAdmin?.email}</span>
            <HelpMenu />
          </div>
        </header>
        <main id="ed-main" className="ed-main" tabIndex={-1}>
          <Suspense fallback={<div className="ed-page"><Loading /></div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <WelcomeGuide />
    </div>
  );
};

export default AppShell;
